// The workflow canvas (F3-1, F3-2): React Flow with the lender node pack. After the_Kit's packages/workflow/src/canvas
// (WorkflowCanvas, KitNode, KitEdge, toFlow), rewritten for five fixed node kinds instead of a node registry: one in-port
// on the left, one labelled out-port per outcome on the right, a dashed line with a chip when it carries a condition.
import '@xyflow/react/dist/base.css'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Background, BackgroundVariant, BaseEdge, Controls, EdgeLabelRenderer, Handle, MiniMap, Position, ReactFlow, ReactFlowProvider,
  applyNodeChanges, getBezierPath, useReactFlow, type Connection, type Edge, type EdgeProps, type FinalConnectionState, type Node, type NodeChange, type NodeProps,
} from '@xyflow/react'
import { Clock, Code2, Flag, GitBranch, Play } from 'lucide-react'
import { notify } from '@/kit/ui'
import { useDevStore } from '@/store/devStore'
import { conditionText, newEdgeId, outputsOf } from '@/lib/flow'
import { endpointLabel } from '@/lib/endpoints'
import type { Batch, FlowEdge, FlowNode } from '@/types'

export const NODE_WIDTH = 210
const ROW = 18

type NodeData = { node: FlowNode; outputs: string[]; state: 'ran' | 'error' | 'stopped' | undefined; issue: boolean; readOnly: boolean }
type EdgeData = { edge: FlowEdge; active: boolean }
type LNode = Node<NodeData, 'lender'>
type LEdge = Edge<EdgeData, 'lender'>

const KIND = {
  start: { Icon: Play, chip: 'bg-primary-soft text-primary', label: 'start · PI info' },
  api: { Icon: Code2, chip: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300', label: 'API' },
  decision: { Icon: GitBranch, chip: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300', label: 'decision' },
  wait: { Icon: Clock, chip: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', label: 'wait' },
  answer: { Icon: Flag, chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', label: 'answer' },
} as const
const BAD_ANSWER = ['rejected', 'error', 'duplicate']

const LenderNodeCard = memo(function LenderNodeCard({ data, selected }: NodeProps<LNode>) {
  const n = data.node
  const k = KIND[n.kind]
  const chip = n.kind === 'answer' && BAD_ANSWER.includes(n.answer ?? '') ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : k.chip
  const sub =
    n.kind === 'api' ? `${n.endpoint ? endpointLabel(n.endpoint) : '(no endpoint)'}${n.waitS ? ` · wait ${n.waitS} s` : ''}${n.retries ? ` · retry ${n.retries}` : ''}`
    : n.kind === 'decision' ? (n.condition?.field ? conditionText(n.condition) : '(no condition)')
    : n.kind === 'wait' ? `${n.seconds ?? 0} s, then on`
    : n.kind === 'answer' ? `${n.answer} · “${(n.message ?? '').slice(0, 34)}${(n.message ?? '').length > 34 ? '…' : ''}”`
    : 'the standard input'
  return (
    <div
      className={`rounded-lg border bg-white shadow-card dark:bg-surface-dark ${selected ? 'border-primary ring-2 ring-primary/30' : data.issue ? 'border-red-300 dark:border-red-700' : data.state === 'ran' ? 'border-primary/60' : 'border-slate-200 dark:border-slate-700'} ${data.state === 'ran' ? 'ring-2 ring-primary/40' : data.state === 'error' ? 'ring-2 ring-red-400/60' : ''}`}
      style={{ width: NODE_WIDTH }}
      data-testid={`node-${n.id}`}
    >
      {n.kind !== 'start' && <Handle type="target" position={Position.Left} id="in" className="handle-in !-left-1.5 !top-6" aria-label="In port" />}
      <div className="flex items-center gap-2 px-2.5 pt-2">
        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded ${chip}`}><k.Icon size={13} /></span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-semibold text-default" title={n.name}>{n.name}</div>
          <div className="truncate font-mono text-2xs text-muted" title={sub}>{sub}</div>
        </div>
      </div>
      <div className="h-2" />
      {data.outputs.length > 0 && (
        <div className="border-t border-slate-100 py-1 dark:border-slate-700">
          {data.outputs.map((o) => (
            <div key={o} className="relative flex items-center justify-end pr-3" style={{ height: ROW }}>
              <span className={`font-mono text-2xs ${o === 'error' ? 'text-red-500' : 'text-slate-600 dark:text-slate-300'}`}>{o}</span>
              <Handle type="source" position={Position.Right} id={`out:${o}`} className="!-right-1.5" aria-label={`Out port ${o}`} isConnectable={!data.readOnly} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
})

const LenderEdge = memo(function LenderEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, markerEnd, selected }: EdgeProps<LEdge>) {
  const [path, lx, ly] = getBezierPath({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition })
  const cond = data?.edge.condition
  return (
    <>
      <BaseEdge id={id} path={path} markerEnd={markerEnd} interactionWidth={14} style={cond ? { strokeDasharray: '5 4' } : undefined} />
      {cond && (
        <EdgeLabelRenderer>
          <span
            className={`nodrag nopan absolute rounded-full border px-2 py-px font-mono text-2xs ${selected ? 'border-primary bg-primary-soft text-primary-text' : 'border-violet-200 bg-white text-violet-700 dark:border-violet-800 dark:bg-slate-800 dark:text-violet-300'}`}
            style={{ transform: `translate(-50%, -50%) translate(${lx}px, ${ly}px)`, pointerEvents: 'none' }}
          >
            if {conditionText(cond)}
          </span>
        </EdgeLabelRenderer>
      )}
    </>
  )
})

const nodeTypes = { lender: LenderNodeCard }
const edgeTypes = { lender: LenderEdge }

export interface FlowCanvasProps {
  nodes: FlowNode[]
  edges: FlowEdge[]
  batch: Batch | undefined
  readOnly: boolean
  selection: { nodeId?: string; edgeId?: string }
  onSelect: (s: { nodeId?: string; edgeId?: string }) => void
  onChange: (nodes: FlowNode[], edges: FlowEdge[]) => void
  /** after Execute: nodes passed (ran) or where it stopped, and the lines taken */
  trace?: { nodes: Record<string, 'ran' | 'error' | 'stopped'>; edges: string[] }
  issueNodes?: string[]
  height?: number | string
  /** false = no border and corners (the full-page editor) */
  framed?: boolean
  /** a palette item dropped on the canvas (dataTransfer "application/lender-hub-node" = JSON { kind, endpoint?, answer? }) */
  onDrop?: (item: { kind: FlowNode['kind']; endpoint?: string; answer?: string }, pos: { x: number; y: number }) => void
}

export const DRAG_TYPE = 'application/lender-hub-node'

function Inner({ nodes, edges, batch, readOnly, selection, onSelect, onChange, trace, issueNodes = [], height = 600, framed = true, onDrop }: FlowCanvasProps) {
  const rf = useReactFlow()
  const build = useCallback(
    (prev: LNode[]): LNode[] =>
      nodes.map((n) => {
        const old = prev.find((p) => p.id === n.id)
        return {
          id: n.id,
          type: 'lender',
          position: n.pos,
          draggable: !readOnly,
          selected: n.id === selection.nodeId,
          ...(old?.measured ? { measured: old.measured } : {}),
          data: { node: n, outputs: outputsOf(n, batch), state: trace?.nodes[n.id], issue: issueNodes.includes(n.id), readOnly },
        }
      }),
    [nodes, batch, readOnly, selection.nodeId, trace, issueNodes],
  )
  const scheme = useDevStore((s) => s.colourScheme)
  const [rfNodes, setRfNodes] = useState<LNode[]>(() => build([]))
  useEffect(() => setRfNodes((prev) => build(prev)), [build])

  const rfEdges = useMemo<LEdge[]>(
    () =>
      edges.map((e) => ({
        id: e.id, type: 'lender', source: e.from, target: e.to, sourceHandle: `out:${e.when}`, targetHandle: 'in',
        selected: e.id === selection.edgeId, className: trace?.edges.includes(e.id) ? 'edge-active' : undefined,
        data: { edge: e, active: !!trace?.edges.includes(e.id) },
      })),
    [edges, selection.edgeId, trace],
  )

  const onNodesChange = useCallback((changes: NodeChange<LNode>[]) => setRfNodes((ns) => applyNodeChanges(changes, ns)), [])
  const connect = useCallback(
    (source: string, sourceHandle: string | null | undefined, target: string) => {
      if (readOnly) return
      if (source === target) return void notify.warning('A node cannot connect to itself')
      if (nodes.find((n) => n.id === target)?.kind === 'start') return void notify.warning('Nothing goes into Start')
      const when = sourceHandle?.startsWith('out:') ? sourceHandle.slice(4) : 'next'
      onChange(nodes, [...edges, { id: newEdgeId(), from: source, when, to: target, condition: null }])
    },
    [readOnly, nodes, edges, onChange],
  )
  const onConnect = useCallback((c: Connection) => c.source && c.target && connect(c.source, c.sourceHandle, c.target), [connect])
  // a line dropped anywhere on a node (not only on its small in-port) joins it too
  const onConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent, state: FinalConnectionState) => {
      if (state.isValid || !state.fromNode || state.fromHandle?.type !== 'source') return
      const point = 'changedTouches' in event ? event.changedTouches[0]! : event
      const el = document.elementFromPoint(point.clientX, point.clientY)?.closest('.react-flow__node')
      const target = el?.getAttribute('data-id')
      if (target) connect(state.fromNode.id, state.fromHandle.id, target)
    },
    [connect],
  )

  return (
    <div
      className={`relative w-full overflow-hidden bg-canvas dark:bg-canvas-dark ${framed ? 'rounded-md border border-slate-200 dark:border-slate-700' : ''}`}
      style={{ height }}
      data-testid="workflow-canvas"
      onDragOver={(e) => { if (onDrop && !readOnly && e.dataTransfer.types.includes(DRAG_TYPE)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' } }}
      onDrop={(e) => {
        if (!onDrop || readOnly) return
        const raw = e.dataTransfer.getData(DRAG_TYPE)
        if (!raw) return
        e.preventDefault()
        const pos = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY })
        onDrop(JSON.parse(raw), { x: Math.round(pos.x - NODE_WIDTH / 2), y: Math.round(pos.y - 20) })
      }}
    >
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        colorMode={scheme}
        onNodesChange={onNodesChange}
        onNodeDragStop={(_, n) => onChange(nodes.map((x) => (x.id === n.id ? { ...x, pos: { x: Math.round(n.position.x), y: Math.round(n.position.y) } } : x)), edges)}
        onConnect={onConnect}
        onConnectEnd={onConnectEnd}
        onNodeClick={(_, n) => onSelect({ nodeId: n.id })}
        onEdgeClick={(_, e) => onSelect({ edgeId: e.id })}
        onPaneClick={() => onSelect({})}
        nodesConnectable={!readOnly}
        nodesDraggable={!readOnly}
        deleteKeyCode={null}
        fitView
        fitViewOptions={{ padding: 0.15, maxZoom: 1 }}
        minZoom={0.25}
        maxZoom={1.6}
      >
        <Background variant={BackgroundVariant.Dots} gap={22} size={1} className="!bg-canvas dark:!bg-canvas-dark" />
        <Controls showInteractive={false} position="bottom-left" />
        <MiniMap pannable zoomable position="bottom-right" nodeStrokeWidth={2} />
      </ReactFlow>
    </div>
  )
}

export function FlowCanvas(props: FlowCanvasProps) {
  return (
    <ReactFlowProvider>
      <Inner {...props} />
    </ReactFlowProvider>
  )
}
